import { createStore, createEffect, createEvent, combine } from 'effector';
import { $notificationStore } from '../notificationStore';
import { ApiUtils } from '@shared/utils';
import { CardsApi } from '@shared/api/rest';
import { Frequency, type CardLimitsResponse } from '@shared/api/rest/cards';
import { type IQueryParams, RequestStrategyEnum } from '@shared/api/models';

const getLimit = (limit: CardLimitsResponse | undefined): number => {
  if (!limit) return 0;

  let amount = 0;
  if (limit.frequency === Frequency.DAILY) {
    amount = limit.dayLimits?.otcAmount || 0;
  } else if (limit.frequency === Frequency.CYCLE) {
    amount = limit.cycleLimits?.otcAmount || 0;
  } else if (limit.frequency === Frequency.YEAR) {
    amount = limit.yearLimits?.otcAmount || 0;
  }
  return limit.currentOTCAmount - amount;
};

const getBalanceEffect = createEffect((params: IQueryParams<number>) => CardsApi.getLimits(params.request, params?.signal));
const clearEvent = createEvent();

const $dataStore = createStore<{ cardId: number; balance: number }[] | null>(null)
  .on(getBalanceEffect.done, (current, data) => {
    if (!current) {
      return [{ cardId: Number(data.params.request), balance: getLimit(data.result.data) }];
    } else {
      const card = current.find((x) => x.cardId === Number(data.params.request));
      if (card) {
        return current.map((balance) => {
          if (balance.cardId === Number(data.params.request)) return { ...balance, balance: getLimit(data.result.data) };
          else return balance;
        });
      } else {
        return [...current, { cardId: Number(data.params.request), balance: getLimit(data.result.data) }];
      }
    }
  })
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(getBalanceEffect.pending, (_, data) => data)
  .on(getBalanceEffect.fail, (_, data) => false)
  .on(getBalanceEffect.done, (_, data) => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(getBalanceEffect, () => null)
  .on(getBalanceEffect.fail, (_, data) => data.error.message)
  .on(getBalanceEffect.done, (_, data) => null)
  .reset(clearEvent);
$errorStore.watch((x) => $notificationStore.events.setError(x));

export const $cardBalanceStore = {
  events: {
    getBalance: ApiUtils.wrapperApi(getBalanceEffect, RequestStrategyEnum.Debounce),
    clear: clearEvent,
  },
  store: combine({
    data: $dataStore,
    loading: $loadingStore,
    error: $errorStore,
  }),
};
